import cn from 'classnames';
import { Dispatch, FC, SetStateAction, useRef } from 'react';

import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { useAppSelector } from '@/shared/hooks';
import { scrollToElementByID } from '@/shared/lib';
import { ToothView } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';

import { JAW, Quartiles } from '../../config';
import { useToothProps } from '../../hooks';
import { getDisplayToothNumber, isLowerJaw, isRightQuartile } from '../../lib';

import styles from './JawQuartile.module.scss';

export type JawQuartileProps = {
  quartile: Quartiles;
  teeth: Tooth[];
  reportID: string;
  ROIList?: number[];
  setROIList?: Dispatch<SetStateAction<number[]>>;
  isROIOpen?: boolean;
  isSimplify?: boolean;
  onToothClick?: (id: string, ISONumber: number) => void;
  className?: string;
  toothOfInterestNumber?: number;
  blackAndWhiteMode?: boolean;
  focusedToothID?: string;
};

/**
 * @deprecated
 */
export const JawQuartile: FC<JawQuartileProps> = (props) => {
  const {
    className,
    quartile,
    reportID,
    ROIList,
    isROIOpen,
    setROIList,
    teeth,
    isSimplify,
    onToothClick,
    toothOfInterestNumber,
    blackAndWhiteMode,
    focusedToothID,
  } = props;

  const jaw = useRef(JAW[quartile]);

  const isRightJaw = isRightQuartile(quartile);
  const isJawLower = isLowerJaw(quartile);
  // TODO: This is impossible if we want to use teeth compilation from many reports
  // We need to make TeethChard that will be take just teeth array as an argument without any information about report

  const { teethWithStatus } = useToothProps({ reportID, teeth });

  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  Object.keys(teethWithStatus).forEach((id: string) => {
    const { Numeration } = teethWithStatus[id];

    const ISONumber = Numeration?.ISO;

    jaw.current = jaw.current.filter((jawItem) => jawItem !== ISONumber);
  });

  const handleToothClick = (
    id: string,
    isActive: boolean = true,
    ISONumber: number,
  ) => {
    if (isROIOpen && ROIList && setROIList) {
      if (ROIList.includes(teethWithStatus[id].Numeration?.ISO ?? 0)) {
        setROIList((prev) =>
          prev.filter(
            (ISONumber) => ISONumber !== teethWithStatus[id].Numeration?.ISO,
          ),
        );
      } else {
        setROIList((prev) => [
          ...prev,
          teethWithStatus[id].Numeration?.ISO ?? 0,
        ]);
      }
    } else if (typeof onToothClick === 'function' && isActive) {
      onToothClick(id, ISONumber);
    } else if (isActive) {
      scrollToElementByID(id);
    }
  };

  return (
    <div
      data-component="tooth-jaw-quartile"
      className={cn(
        styles.container,
        isJawLower && styles.isLowerJaw,
        className,
      )}
    >
      <div
        className={cn(styles.toothContainer, isRightJaw && styles.isRightJaw)}
      >
        {jaw.current.map((jawItem) => (
          <div
            key={jawItem}
            className={cn(
              styles[`t${jawItem.toString().slice(-1)}`],
              isSimplify ? styles.simpleNotDetected : styles.notDetected,
            )}
          />
        ))}

        {Object.keys(teethWithStatus).map((id) => {
          // TODO: move ToothView out of JawQuartile
          const { ID, Numeration, toothStatus, IsApproved } =
            teethWithStatus[id];

          const ISONumber = Numeration?.ISO ?? 0;

          const toothNumberView = getDisplayToothNumber(
            ISONumber,
            dentalNotationFormat,
          );

          const isToothActive = ROIList ? ROIList?.includes(ISONumber) : true;

          return (
            <ToothView
              isApproved={IsApproved}
              key={ID}
              id={id}
              className={cn(styles[`t${ISONumber.toString().slice(-1)}`])}
              isLowerJaw={isJawLower}
              ISONumber={ISONumber}
              viewNumber={toothNumberView}
              onClick={({ id: toothID, isActive, ISONumber }) =>
                handleToothClick(toothID, isActive, ISONumber)
              }
              isActive={isToothActive}
              status={toothStatus}
              isROIOpen={isROIOpen}
              isSimplify={isSimplify}
              toothOfInterestNumber={toothOfInterestNumber}
              blackAndWhiteMode={blackAndWhiteMode}
              isFocused={ID === focusedToothID}
            />
          );
        })}
      </div>
    </div>
  );
};
